import type { MentoriaModelSpec } from './types';

/**
 * The Claude models MentorIA can run on, with Bedrock prices per million tokens (USD,
 * us-east-1) from the cost log. Which ones answer in the account is checked on dev, not here:
 * on 2026-09-28 Haiku 4.5 and Sonnet 4.6 answered, Sonnet 5 and Opus 5 refused with
 * `AccessDenied` ("not available for this account"), so the default stays on Haiku 4.5 and a
 * contract may name another model once AWS grants it (ADR-0009).
 */
export const MENTORIA_MODELS: readonly MentoriaModelSpec[] = [
  {
    key: 'haiku-4-5',
    label: 'Claude Haiku 4.5',
    modelId: 'us.anthropic.claude-haiku-4-5-20251001-v1:0',
    inputPerMillion: 1,
    outputPerMillion: 5,
    request: { temperature: 0.4 },
  },
  {
    key: 'sonnet-4-6',
    label: 'Claude Sonnet 4.6',
    modelId: 'us.anthropic.claude-sonnet-4-6',
    inputPerMillion: 3,
    outputPerMillion: 15,
    request: { temperature: 0.4 },
  },
  {
    key: 'sonnet-5',
    label: 'Claude Sonnet 5',
    modelId: 'us.anthropic.claude-sonnet-5',
    inputPerMillion: 3,
    outputPerMillion: 15,
    // The 5 series rejects sampling parameters and thinks by default; a tutor's short answers
    // need neither, so the request carries no temperature and turns thinking off.
    request: { thinking: 'disabled' },
  },
  {
    key: 'opus-5',
    label: 'Claude Opus 5',
    modelId: 'us.anthropic.claude-opus-5',
    inputPerMillion: 5,
    outputPerMillion: 25,
    request: { thinking: 'disabled' },
  },
];

/**
 * The model MentorIA runs on unless a contract or the environment says otherwise: the one that
 * answers in the account today at the lowest measured cost (0.0022 USD per question on dev,
 * 2026-09-23).
 */
export const MENTORIA_DEFAULT_MODEL_ID = MENTORIA_MODELS[0]?.modelId ?? '';

/**
 * Whether an id names a model of the catalog.
 *
 * @param {string | null | undefined} modelId -- A Bedrock inference profile id.
 * @returns `true` when MentorIA knows the model.
 */
export const isMentoriaModelId = (modelId: string | null | undefined): modelId is string =>
  typeof modelId === 'string' && MENTORIA_MODELS.some((model) => model.modelId === modelId);
